import { getUrlApi } from "../utilities/util";
import ApiHandler from "./ApiHandlers/ApiHandler";

const baseURL = String(getUrlApi());

export default class FmeaAiagVdaApi {
  // ---- Step 1 (standardized routes) ----
  static async GetStep1(fmeaId: string): Promise<any> {
    const response = await ApiHandler(baseURL).get(`/fmeas/${fmeaId}/steps/1`);
    return response.data;
  }

  static async UpdateStep1(fmeaId: string, data: {
    objective: string;
    scope: string;
    team: string;
    completedBy: string;
  }): Promise<any> {
    const response = await ApiHandler(baseURL).put(`/fmeas/${fmeaId}/steps/1`, data);
    return response.data;
  }

  // ---- New / Refined Endpoints per updated Step 2 specification ----
  // Base: /api/fmeas/{fmeaId}/steps/2
  static async GetStep2FullTree(fmeaId: string): Promise<any> {
    const response = await ApiHandler(baseURL).get(`/fmeas/${fmeaId}/steps/2/tree`);
    return response.data;
  }

  static async CreateProcessItem(
    fmeaId: string,
    payload: { name?: string; existingItemId?: string; createdBy: string; plantId: string },
  ): Promise<any> {
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/2/items`, payload);
    return response.data;
  }

  static async SearchProcessItemsByPlant(
    plantId: string,
    search: string,
  ): Promise<Array<{ id: string; name: string }>> {
    const response = await ApiHandler(baseURL).get(`/plants/${plantId}/fmea-items`, {
      params: { search },
    });
    return response.data;
  }

  static async UpdateProcessItem(fmeaId: string, itemId: string, name: string, updatedBy: string): Promise<any> {
    const response = await ApiHandler(baseURL).put(`/fmeas/${fmeaId}/steps/2/items/${itemId}`, {
      id: itemId,
      name,
      updatedBy,
    });
    return response.data;
  }

  static async DeleteProcessItem(fmeaId: string, itemId: string): Promise<any> {
    const response = await ApiHandler(baseURL).delete(`/fmeas/${fmeaId}/steps/2/items/${itemId}`);
    return response.data;
  }

  static async CreateProcessOperation(params: {
    fmeaId: string;
    itemId: string;
    operationId: string;
    type: number; // OperationTypeEnum
    createdBy: string;
    elements?: Array<{ elementDescription: string; cause6m: number; elementType: number; createdBy?: string }>;
  }): Promise<any> {
    const { fmeaId, itemId, ...body } = params;
    if (body.elements) {
      body.elements = body.elements.map(el => ({ ...el, createdBy: el.createdBy || body.createdBy }));
    }
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/2/items/${itemId}/operations`, body);
    return response.data;
  }

  static async DeleteProcessOperation(fmeaId: string, operationId: string): Promise<any> {
    const response = await ApiHandler(baseURL).delete(`/fmeas/${fmeaId}/steps/2/operations/${operationId}`);
    return response.data;
  }

  static async CreateWorkElement(
    fmeaId: string,
    processStepId: string,
    payload: { elementDescription: string; cause6m: number; elementType: number; createdBy: string },
  ): Promise<any> {
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/2/operations/${processStepId}/elements`, payload);
    return response.data;
  }

  static async UpdateWorkElement(fmeaId: string, elementId: string, payload: { elementDescription: string; cause6m: number; elementType: number; updatedBy: string }): Promise<any> {
    const response = await ApiHandler(baseURL).put(`/fmeas/${fmeaId}/steps/2/elements/${elementId}`, payload);
    return response.data;
  }

  static async DeleteWorkElement(fmeaId: string, elementId: string): Promise<any> {
    const response = await ApiHandler(baseURL).delete(`/fmeas/${fmeaId}/steps/2/elements/${elementId}`);
    return response.data;
  }

  static async ReorderOperations(fmeaId: string, itemId: string, orderedIds: string[]): Promise<any> {
    const response = await ApiHandler(baseURL).put(`/fmeas/${fmeaId}/steps/2/items/${itemId}/operations/reorder`, {
      orderedIds,
    });
    return response.data;
  }

  static async ValidateStructureAnalysis(fmeaId: string): Promise<{ valid: boolean; message?: string }> {
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/2/validate`);
    return response.data;
  }

  static async CompleteStructureAnalysis(fmeaId: string, userId: string): Promise<{ completed: boolean }> {
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/2/complete`, { userId });
    return response.data;
  }

  static async ExportStep2Pdf(fmeaId: string): Promise<Blob> {
    const response = await ApiHandler(baseURL).get(`/fmeas/${fmeaId}/steps/2/export/pdf`, {
      responseType: "blob",
    });
    return response.data ?? response;
  }

  // ---- Step 3 – Function Analysis ----
  static async GetStep3FunctionAnalysis(
    fmeaId: string,
    search?: string,
  ): Promise<any> {
    const response = await ApiHandler(baseURL).get(`/fmeas/${fmeaId}/steps/3/analysis`, {
      params: search ? { search } : {},
    });
    return response.data;
  }

  static async CreateFunctionAssociation(
    fmeaId: string,
    payload: {
      stepId: string;
      targetId: string;
      targetType: number;
      description: string;
      functionType?: number | null;
      createdBy?: string | null;
    },
  ): Promise<any> {
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/3/functions`, payload);
    return response.data;
  }

  static async AddFunctionRequirement(
    fmeaId: string,
    functionId: string,
    payload: { description: string; createdBy: string },
  ): Promise<any> {
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/3/functions/${functionId}/requirements`, payload);
    return response.data;
  }

  static async ValidateFunctionAnalysis(fmeaId: string): Promise<{ valid: boolean; message?: string }> {
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/3/validate`);
    return response.data;
  }

  static async CompleteFunctionAnalysis(
    fmeaId: string,
    userId: string,
  ): Promise<{ completed: boolean }> {
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/3/complete`, { userId });
    return response.data;
  }

  // ---- Step 4 – Failure Analysis ----
  static async GetStep4FailureAnalysis(
    fmeaId: string,
    search?: string,
  ): Promise<any> {
    const response = await ApiHandler(baseURL).get(`/fmeas/${fmeaId}/steps/4/analysis`, {
      params: { search: search ?? '' },
    });
    return response.data;
  }

  static async CreateFailureAnalysisEntry(
    fmeaId: string,
    payload: {
      functionId: string;
      effect: string;
      mode: string;
      causeDescription: string;
      createdBy?: string | null;
    },
  ): Promise<any> {
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/4/failures`, payload);
    return response.data;
  }

  static async UpdateFailureAnalysisEntry(
    fmeaId: string,
    failureId: string,
    payload: {
      effect: string;
      mode: string;
      causeDescription: string;
      updatedBy?: string | null;
    },
  ): Promise<any> {
    const response = await ApiHandler(baseURL).put(`/fmeas/${fmeaId}/steps/4/failures/${failureId}`, payload);
    return response.data;
  }

  static async DeleteFailureAnalysisEntry(
    fmeaId: string,
    failureId: string,
  ): Promise<any> {
    const response = await ApiHandler(baseURL).delete(`/fmeas/${fmeaId}/steps/4/failures/${failureId}`);
    return response.data;
  }

  static async ValidateFailureAnalysis(fmeaId: string): Promise<{ valid: boolean; message?: string }> {
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/4/validate`);
    return response.data;
  }

  static async CompleteFailureAnalysis(
    fmeaId: string,
    userId: string,
  ): Promise<{ completed: boolean }> {
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/4/complete`, { userId });
    return response.data;
  }

  static async UploadDocumentStep1(fmeaId: string, formData: FormData): Promise<any> {
    const response = await ApiHandler(baseURL).post(`/fmeas/${fmeaId}/steps/1/documents`, formData);
    return response.data;
  }

  static async GetStep1Attachments(fmeaId: string): Promise<any> {
    const response = await ApiHandler(baseURL).get(`/fmeas/${fmeaId}/steps/1/attachments`);
    return response.data;
  }
}